import React, { useCallback, KeyboardEvent, useMemo } from "react";
import { Button, tokens } from "@fluentui/react-components";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useStyles } from "./navigationHeaderStyles";
import { appInsightsEvents, trackEvent, useAppStore } from "../../../common";
import { Cart24Regular, ChevronLeft16Regular } from "@fluentui/react-icons";
import { Breadcrumb } from "../Breadcrumb/Breadcrumb";
import { WebAgentToggleButton } from "../../WebAgent/WebAgentToggleButton";

export const NavigationHeader = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { cafeId } = useParams();
  const cart = useAppStore((state) => state.cart.value);
  const activeCafeId = cafeId ?? cart?.cafeId;
  const getCartItemCount = useCallback(() => {
    return cart ? cart.items?.length : 0;
  }, [cart]);
  const [appInsights, context] = useAppStore((state) => [state.appInsights, state.context]);
  const diningAgentEnabled = useAppStore((state) => state.featureFlags.diningAgentEnabled);

  // Determine if page should show breadcrumbs
  const shouldShowBreadcrumbs = useMemo(() => {
    // Show breadcrumbs on cafe, station and menu item details pages only
    return (
      location.pathname.match(/^\/cafe\/[^/]+$/) || // Cafe page
      location.pathname.match(/\/cafe\/[^/]+\/station\/[^/]+$/) || // Station page
      location.pathname.match(/\/cafe\/[^/]+\/station\/[^/]+\/item\/[^/]+$/)
    );
  }, [location.pathname]);

  // Determine if buttons should be shown
  const isCafePage = location.pathname.match(/^\/cafe\/[^/]+$/); // Check if we're on a base cafe page
  const showBackButton = !shouldShowBreadcrumbs; // Don't show back button on pages with breadcrumbs
  const showCartButton = !isCafePage; // Don't show cart button on base cafe pages

  const handleKeyDownBackButton = (event: KeyboardEvent) => {
    if (event.code === "Space" || event.code === "Enter") {
      navigateOnBackButton();
    }
  };

  // Always use hierarchical fallback for navigation
  const navigateOnBackButton = () => {
    const path = window.location.pathname;
    let targetPath: string | null = null;

    // Edit item page: /cafe/:cafeId/station/:stationId/item/:itemId/edit
    const editItemMatch = path.match(/\/cafe\/([^/]+)\/station\/([^/]+)\/item\/[^/]+\/edit/);
    if (editItemMatch) {
      const cafeIdFromUrl = editItemMatch[1];
      const stationIdFromUrl = editItemMatch[2];
      // Navigate back to station page after editing an item
      targetPath = `/cafe/${cafeIdFromUrl}/station/${stationIdFromUrl}`;
    }

    // Menu item detail: /cafe/:cafeId/station/:stationId/item/:itemId
    const menuItemMatch = path.match(/\/cafe\/([^/]+)\/station\/([^/]+)\/item\/[^/]+/);
    if (!targetPath && menuItemMatch) {
      const cafeIdFromUrl = menuItemMatch[1];
      const stationIdFromUrl = menuItemMatch[2];
      targetPath = `/cafe/${cafeIdFromUrl}/station/${stationIdFromUrl}`;
    }

    // Checkout page: /cafe/:cafeId/checkout
    const checkoutMatch = path.match(/\/cafe\/([^/]+)\/checkout/);
    if (!targetPath && checkoutMatch) {
      const cafeIdFromUrl = checkoutMatch[1];
      // Use stationId from the first cart item; do not fall back to localStorage or params.
      // Orders can contain items from multiple stations — choose the first item's station.
      let stationIdToUse: string | null = null;
      if (cart && cart.items && cart.items.length > 0) {
        stationIdToUse = cart.items[0].stationId ?? null;
      }
      if (stationIdToUse) {
        targetPath = `/cafe/${cafeIdFromUrl}/station/${stationIdToUse}`;
      } else {
        targetPath = `/cafe/${cafeIdFromUrl}`;
      }
    }

    // Order details: /orders/past/:orderId
    const ordersDetailsMatch = path.match(/^\/orders\/past\/([^/]+)$/);
    if (!targetPath && ordersDetailsMatch) {
      // Navigate back to the order history list
      targetPath = `/orders/past`;
    }

    // Cafe-specific order details: /cafe/:cafeId/order/:orderId
    const cafeOrderMatch = path.match(/\/cafe\/([^/]+)\/order\/[^/]+/);
    if (!targetPath && cafeOrderMatch) {
      const cafeIdFromUrl = cafeOrderMatch[1];
      // Navigate back to the cafe page from a cafe-scoped order details view
      targetPath = `/cafe/${cafeIdFromUrl}`;
    }

    // Order history list: /orders/past
    const ordersListMatch = path.match(/^\/orders\/past$/);
    if (!targetPath && ordersListMatch) {
      // Navigate back to the cafe page
      targetPath = activeCafeId ? `/cafe/${activeCafeId}` : `/`;
    }

    // Station page: /cafe/:cafeId/station/:stationId
    const stationMatch = path.match(/\/cafe\/([^/]+)\/station\/[^/]+/);
    if (!targetPath && stationMatch) {
      const cafeIdFromUrl = stationMatch[1];
      targetPath = `/cafe/${cafeIdFromUrl}`;
    }

    // Cafe page: /cafe/:cafeId
    const cafeMatch = path.match(/\/cafe\/([^/]+)/);
    if (!targetPath && cafeMatch) {
      targetPath = `/`;
    }

    if (targetPath) {
      navigate(targetPath, { replace: true });
      // Emit a single telemetry event for the back navigation
      trackEvent(appInsights, appInsightsEvents.Usage.Actions.NavigateBack, context, {
        componentName: "NavigationHeader",
        cafeId: cafeId ?? activeCafeId,
      });
      return;
    }

    // Default: DO NOT fall back to browser history.
    // behavior for any new routes instead of silently using history.back().
    throw new Error(
      `NavigationHeader: No back navigation behavior defined for path "${path}". ` +
        `Add a case in navigateOnBackButton for this route.`,
    );
  };
  const onBackClick = useCallback(() => {
    navigateOnBackButton();
  }, []);

  const onCartClick = useCallback(() => {
    if (!activeCafeId) {
      return;
    }

    navigate(`/cafe/${activeCafeId}/checkout`);
    trackEvent(appInsights, appInsightsEvents.Usage.Actions.NavigateToCheckout, context, {
      componentName: "NavigationHeader",
      cafeId: activeCafeId,
    });
  }, [appInsights, activeCafeId, context, navigate]);

  const cartItemCount = getCartItemCount();

  return (
    <>
      <header
        data-testid="navigationHeader"
        className={isCafePage ? styles.containerCafePage : styles.container}
      >
        <div className={styles.contentWrapper} data-testid="navigationHeaderContent">
          <div className={styles.leftSection}>
            {showBackButton && (
              <div
                onClick={onBackClick}
                data-testid={"backBtn"}
                aria-label={"back"}
                tabIndex={0}
                role="button"
                onKeyDown={(event) => {
                  if (event.code === "Space" || event.code === "Enter") {
                    event.preventDefault();
                    handleKeyDownBackButton(event);
                  }
                }}
                autoFocus
              >
                <ChevronLeft16Regular className={styles.backButtonIcon} aria-label={"Go back"} />
                <span className={styles.backButtonText}>{"Back"}</span>
              </div>
            )}
            {shouldShowBreadcrumbs && (
              <div className={styles.breadcrumbContainer}>
                <Breadcrumb />
              </div>
            )}
          </div>
          <div className={styles.rightSection}>
            {diningAgentEnabled && <WebAgentToggleButton />}
            {/* <div className={styles.feedbackButtonContainer} data-testid="feedbackBtn">
              <GeneralFeedbackButton />
            </div> */}
            {showCartButton && (
              <div className={styles.cartButtonContainer}>
                <Button
                  aria-label={`Cart, ${cartItemCount} ${cartItemCount === 1 ? "item" : "items"}`}
                  // Keep the empty cart reachable so screen readers can still announce it as unavailable.
                  disabledFocusable={!cartItemCount || !activeCafeId}
                  color={tokens.colorBrandForeground2}
                  appearance="primary"
                  className={styles.cartButton}
                  iconPosition="before"
                  icon={<Cart24Regular />}
                  onClick={onCartClick}
                  shape="rounded"
                  size="small"
                  data-testid="cartBtn"
                >{`${cartItemCount}`}</Button>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
};
